/* Globalna tema aplikacije (light / dark / retro / retrodark).
 * Primenjuje se preko atributa data-theme na <html> (koren): dark-theme.css nosi raspored boja za
 * tamnu i retro temu, retro-theme.css donosi vrednosti tokena i pikselski izgled.
 * Perzistira lokalno (localStorage.appTheme) za instant primenu bez treptaja, a po korisniku se čuva u bazi
 * (auth.Users.tema) preko /users/theme; pri loginu se učita iz data.user.tema.
 *
 * RETRO IMA DVA LICA — svetlo („dnevni ekran", cijan podloga) i tamno („noćni
 * ekran", podloga boje ugašenog monitora). Oba su ista tema, pa se u DOM
 * upisuju kao JEDAN `data-theme="retro"` plus `data-retro="light|dark"`.
 *
 * Zašto tako, a ne dve vrednosti `data-theme`: ceo pikselski izgled (okviri,
 * tvrde senke, mono pismo, raster) napisan je kao `[data-theme="retro"]` i
 * važi za obe varijante — ovako se ne duplira ni jedno pravilo. Razlikuju se
 * samo VREDNOSTI tokena, a njih preuzima blok sa jačim selektorom
 * `:root[data-theme="retro"][data-retro="dark"]`. */

const THEMES = ['light', 'dark', 'retro', 'retrodark'];

/* Tema -> par atributa na <html>. */
const ATRIBUTI = {
    light:     { tema: 'light', retro: null },
    dark:      { tema: 'dark',  retro: null },
    retro:     { tema: 'retro', retro: 'light' },
    retrodark: { tema: 'retro', retro: 'dark' },
};

export const getStoredTheme = () => {
    const t = localStorage.getItem('appTheme');
    return THEMES.includes(t) ? t : 'light';
};

/* Da li je tema iz retro porodice — koristi se u prekidačima. */
export const jeRetro = (theme) => theme === 'retro' || theme === 'retrodark';

/* Da li je tema tamna (uključujući tamni retro). */
export const jeTamna = (theme) => theme === 'dark' || theme === 'retrodark';

export const applyTheme = (theme) => {
    const t = THEMES.includes(theme) ? theme : 'light';
    const a = ATRIBUTI[t];
    document.documentElement.setAttribute('data-theme', a.tema);
    if (a.retro) document.documentElement.setAttribute('data-retro', a.retro);
    else document.documentElement.removeAttribute('data-retro');
    localStorage.setItem('appTheme', t);
    return t;
};

/* Promena teme sa prelazom: nova tema se otkriva krugom koji se širi iz tačke
   na kojoj je korisnik kliknuo (prekidač). Radi preko View Transitions API-ja —
   pregledač snimi staru sliku strane i nova se iscrta preko nje, pa animiramo
   samo `clip-path` NOVOG sloja.

   Zašto ovako, a ne CSS tranzicija na bojama: tema menja stotine pravila u
   desetinama fajlova (pozadine, okviri, senke, gradijenti). Tranzicija na
   svakom od njih bila bi i neizvodljiva i spora; ovako pregledač animira dve
   slike i trošak je isti bez obzira koliko se boja promenilo.

   Pada nazad na običnu promenu kad: pregledač nema API (Firefox/Safari), kad
   je u sistemu uključeno „smanji pokret", ili kad tačka nije poznata. */
export const applyThemeWithReveal = (theme, origin) => {
    const t = THEMES.includes(theme) ? theme : 'light';
    const smePokret = !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (!document.startViewTransition || !smePokret || !origin) return applyTheme(t);

    const prelaz = document.startViewTransition(() => applyTheme(t));
    prelaz.ready
        .then(() => {
            const { x, y } = origin;
            /* Poluprečnik do najdaljeg ugla ekrana — krug mora da pokrije sve. */
            const r = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));
            document.documentElement.animate(
                { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${r}px at ${x}px ${y}px)`] },
                { duration: 620, easing: 'cubic-bezier(.42,0,.25,1)', pseudoElement: '::view-transition-new(root)' }
            );
        })
        .catch(() => { /* prelaz prekinut (npr. brzo dupli klik) — tema je već primenjena */ });

    return t;
};
